// Text drawn above a bar: the main figure plus an optional qualifier, e.g. "1,637" and "73%".
export interface BarLabel {
  value: string;
  detail?: string;
}

/** The label as a single string, e.g. "1,637 (73%)" -- used where there is no width constraint. */
export const formatBarLabel = ({ value, detail }: BarLabel): string =>
  detail === undefined ? value : `${value} (${detail})`;

// Layouts from fullest to most compact.
const LABEL_LAYOUTS: ((label: BarLabel) => string[])[] = [
  label => [formatBarLabel(label)],
  ({ value, detail }) => (detail === undefined ? [value] : [value, `(${detail})`]),
  ({ value }) => [value]
];

/**
 * The lines to draw above each bar. Every bar uses the same layout -- the fullest one in which
 * every label still fits in `slotWidth` -- so the chart never mixes one-line and stacked labels:
 * "1,637 (73%)" on one line, else the figure above "(73%)", else the figure alone.
 * `measure` returns the rendered width of a string; it is passed in so this stays pure.
 */
export const getUniformBarLabelLines = (
  labels: BarLabel[],
  slotWidth: number,
  measure: (text: string) => number
): string[][] => {
  const layout =
    LABEL_LAYOUTS.find(candidate =>
      labels.every(label => candidate(label).every(line => measure(line) <= slotWidth))
    ) ?? LABEL_LAYOUTS[LABEL_LAYOUTS.length - 1];
  return labels.map(layout);
};

export const TICK_ROTATION_DEGREES = 40;
const DEFAULT_AXIS_HEIGHT = 30;
// Names longer than this are cut even when rotated, so the axis never takes over the chart.
const MAX_ROTATED_TICK_WIDTH = 96;
// Clear space kept between neighbouring category names.
const TICK_PADDING = 4;
// Room under rotated names for the font height plus a little breathing space.
const ROTATED_AXIS_EXTRA_HEIGHT = 12;

export interface CategoryTickLayout {
  rotate: boolean;
  // Widest a name may render before it is cut with an ellipsis.
  maxWidth: number;
  axisHeight: number;
}

/**
 * How to lay out the category names under the bars. Names sit flat when every one fits in its
 * bar's `pitch` (the horizontal room per bar); if any doesn't, all names are rotated so they stay
 * readable in full instead of being chopped to a few letters.
 */
export const getCategoryTickLayout = (
  names: string[],
  pitch: number,
  fontSize: number,
  measure: (text: string) => number
): CategoryTickLayout => {
  const flatWidth = Math.max(0, pitch - TICK_PADDING);
  if (names.every(name => measure(name) <= flatWidth)) {
    return { rotate: false, maxWidth: flatWidth, axisHeight: DEFAULT_AXIS_HEIGHT };
  }

  const longest = Math.min(MAX_ROTATED_TICK_WIDTH, Math.max(...names.map(measure)));
  const rotatedHeight = Math.sin((TICK_ROTATION_DEGREES * Math.PI) / 180) * longest;
  return {
    rotate: true,
    maxWidth: MAX_ROTATED_TICK_WIDTH,
    axisHeight: Math.ceil(rotatedHeight + fontSize + ROTATED_AXIS_EXTRA_HEIGHT)
  };
};

/** `text`, cut with an ellipsis if needed so it renders no wider than `maxWidth`. */
export const truncateToWidth = (
  text: string,
  maxWidth: number,
  measure: (text: string) => number
): string => {
  if (measure(text) <= maxWidth) return text;
  let end = text.length;
  while (end > 1 && measure(`${text.slice(0, end)}…`) > maxWidth) {
    end -= 1;
  }
  return `${text.slice(0, end)}…`;
};
